import { Children, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { labelOf, RISK_IMPACT, RISK_LABEL, type Label, type StateChip } from '@grc/shared/vocabulary';
import type { Loadable } from './api';

/** A status in the approved vocabulary. Tone drives colour; only 'off_track' may render red. */
export function Status({ map, value }: { map: Record<string, Label>; value: string | null | undefined }) {
  const l = labelOf(map, value);
  return <span className={`chip tone-${l.tone}`}>{l.label}</span>;
}

/** Every number shown says what it represents. */
export function Measure({ label, children }: { label: string; children: ReactNode }) {
  return (
    <figure className="measure">
      <div className="measure-value">{children}</div>
      <figcaption>{label}</figcaption>
    </figure>
  );
}

/** A way back, at the top left. Home does not use one. */
export function PageBack({ to, children }: { to: string; children: ReactNode }) {
  return (
    <p className="page-back">
      <Link to={to}>{children}</Link>
    </p>
  );
}

export function PageHeader({ title, lede, eyebrow }: { title: string; lede?: ReactNode; eyebrow?: string }) {
  return (
    <header className="page-header">
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <h1>{title}</h1>
      {lede && <p className="lede">{lede}</p>}
    </header>
  );
}

export function Loading({ what }: { what: string }) {
  return (
    <p role="status" aria-live="polite" className="muted">
      Loading {what}…
    </p>
  );
}

export function Failure({ error, onRetry }: { error: { message: string }; onRetry?: () => void }) {
  return (
    <div role="alert" className="notice">
      <p>{error.message}</p>
      {onRetry && (
        <button className="pill secondary-dark" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

/** Renders loading, error or content for a Loadable. */
export function Load<T>({ state, what, children }: { state: Loadable<T>; what: string; children: (data: T) => ReactNode }) {
  if (state.loading && !state.data) return <Loading what={what} />;
  if (state.error) return <Failure error={state.error} onRetry={state.reload} />;
  if (!state.data) return null;
  return <>{children(state.data)}</>;
}

export function Card({ title, children, id }: { title: string; children: ReactNode; id?: string }) {
  const hid = id ?? title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  return (
    <section className="card" aria-labelledby={hid}>
      <h2 id={hid}>{title}</h2>
      {children}
    </section>
  );
}

export const waveLabel = (w: number | null) => (w ? `BTI Wave ${w}` : 'Not in a BTI wave');

/** Date-only values ("2026-09-23") are calendar dates, not UTC midnight: read them at noon so no time zone shifts the day. */
export const formatDate = (iso: string | null | undefined) =>
  iso ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T12:00:00` : iso).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Not recorded';

/** BTI Resilience Policy wave as a coloured tag; nothing for controls outside the waves. */
export function WaveTag({ wave }: { wave: number | null }) {
  if (!wave) return null;
  return <span className={`tag wave${wave}`}>BTI Wave {wave}</span>;
}

/** Risk and impact of a control (National guidance, packages/shared/src/risk-impact.ts). */
export function RiskChip({ controlId, why = false }: { controlId: string; why?: boolean }) {
  const r = RISK_IMPACT[controlId];
  if (!r) return null;
  return (
    <>
      <span className={`risk risk-${r.level}`} title={r.why}>
        {RISK_LABEL[r.level]}
      </span>
      {why && <div className="muted small">{r.why}</div>}
    </>
  );
}

/**
 * The line beside a risk acceptance. While it waits for an executive's approval nothing has been decided yet, so it
 * says who it waits for (bug fix, 2026-10-03: it read "Decided {date}" while still awaiting approval).
 */
export function acceptanceLine(a: { status: string; decidedOn: string; approverName: string; approverRole: string; reviewDueOn: string }): string {
  if (a.status === 'pending_approval') return `Recorded ${formatDate(a.decidedOn)}, awaiting approval by ${a.approverName} (${a.approverRole}); review by ${formatDate(a.reviewDueOn)}`;
  return `Decided ${formatDate(a.decidedOn)} by ${a.approverName} (${a.approverRole}); review by ${formatDate(a.reviewDueOn)}`;
}

/** A state chip in informative words (never a verdict; red only for a committed plan off track). */
export function StateChipView({ chip }: { chip: (Pick<StateChip, 'label'> & { tone: string; /** A hover note, such as the earlier figure a change is measured from. */ title?: string }) | null | undefined }) {
  if (!chip) return null;
  return (
    <span className={`chip tone-${chip.tone} lead-state`} title={chip.title}>
      {chip.label}
    </span>
  );
}

/** "Source: ...", the one form every figure's source takes on the page (F2). */
export function SourceLine({ source, className = '' }: { source: string; className?: string }) {
  return <p className={`lead-source ${className}`.trim()}>Source: {source}</p>;
}

/**
 * The one structured card (5D, DEC-49; UX-09; 5E, DEC-58): the question as the section heading, the answer large below
 * it with its state chip beside it, one line saying what it means, where it comes from, and anything under it after a
 * divider. Every question on Leadership, the National view's Summary and the control page is drawn by this component,
 * in one heading style (.answer-q) and one answer style, so the pages cannot drift. The looks differ only in their
 * frame: the section look (Leadership's sections, the control page), the opening look (the answer cards at the top of
 * a Leadership tab) and the snapshot look (the National view's cards, with their short name).
 */
export function AnswerSection({ id, look = 'section', level, name, question, answer, sub, chip, meaning, note, source, chart, link, className = '', foot = false, wave, half = false, anchor, children }: {
  /** A section that shares its row with another (it does not take the full width of the grid). */
  half?: boolean;
  /** The section's own id, for links to it ("#acceptances"). */
  anchor?: string;
  id: string;
  look?: 'section' | 'snapshot' | 'opening';
  /** The heading's level: 3 by default (4 in the snapshot look); 2 where the section sits right under the page title. */
  level?: 2 | 3 | 4;
  /** Said after the meaning line and before the source (the opening card's unsorted note, BTI's position count). */
  note?: ReactNode;
  /** BTI's wave, for the BTI tab's sections (data-wave). */
  wave?: number;
  /**
   * The meaning and source after the parts below the divider instead of before them, for a section whose parts must
   * stay on the first screen (the Summary's headline tiles, NEXT PASS 2): the heading row still leads.
   */
  foot?: boolean;
  /** The snapshot look's short name, above the question; the card's accessible name. */
  name?: string;
  question: string;
  answer?: ReactNode;
  sub?: ReactNode;
  chip?: (Pick<StateChip, 'label'> & { tone: string; title?: string }) | null;
  meaning?: ReactNode;
  source?: string;
  chart?: ReactNode;
  link?: { to: string; label: string };
  className?: string;
  children?: ReactNode;
}) {
  const snap = look === 'snapshot';
  const opening = look === 'opening';
  const H = `h${level ?? (snap ? 4 : 3)}` as 'h2' | 'h3' | 'h4';
  const frame = snap ? 'card snap' : opening ? `lead-answer lead-answer-${id}` : half ? 'card lead-section' : 'card lead-wide lead-section';
  const headingId = snap ? undefined : opening ? `open-${id}-h` : id;
  // The divider separates the answer from what is under it; with nothing above it (a section that is only a question
  // and its content) there is nothing to separate (5E).
  const above = Boolean(answer || chip || (!foot && (meaning || source)));
  return (
    <section className={`${frame} ${className}`.trim()} aria-labelledby={name ? `${id}-name` : headingId} id={snap ? id : anchor} data-wave={wave}>
      {name && (
        // 5E NEXT PASS 1 (E5): no small label above the question. The short name stays the region's accessible name only;
        // the question is the one visible heading.
        <p className="snap-name sr-only" id={`${id}-name`}>
          {name}
        </p>
      )}
      <H id={headingId} className={snap ? 'snap-question answer-q' : 'lead-q answer-q'}>
        {question}
      </H>
      {snap ? (
        <p className="snap-figure">
          <strong>{answer}</strong>
          {sub && <span className="snap-sub">{sub}</span>}
        </p>
      ) : (
        (answer || chip) && (
          <p className="lead-a">
            {answer && <span className="lead-a-value">{answer}</span>} <StateChipView chip={chip} />
          </p>
        )
      )}
      {/* 5E (DEC-58): the chip sits beside the answer in every look; in the snapshot look it follows the figure. */}
      {snap && chip && (
        <p className="snap-state">
          <StateChipView chip={chip} />
        </p>
      )}
      {chart}
      {!foot && meaning && <p className={snap ? 'snap-means' : opening ? 'lead-headline' : 'lead-meaning'}>{meaning}</p>}
      {note}
      {!foot && source && <SourceLine source={source} />}
      {/* Children that render nothing (false, null) are no content, so they draw no divider either. */}
      {Children.toArray(children).length > 0 && (
        <>
          {above && <hr className="lead-divider" />}
          {children}
        </>
      )}
      {foot && meaning && <p className="lead-meaning">{meaning}</p>}
      {foot && source && <SourceLine source={source} />}
      {link && (
        <p className="snap-link">
          <Link to={link.to}>{link.label}</Link>
        </p>
      )}
    </section>
  );
}
